"use client";

import { c as _c } from "react/compiler-runtime";
import { forwardRef, useState } from "react";
import { IoLockClosedOutline } from "react-icons/io5";
import { Button, Input, Label } from "@/components/ui";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useUser } from "@/contexts/user-provider";
import { useStaffPinUnlock } from "@/hooks/use-staff-pin-unlock";
import { useToast } from "@/lib/use-toast";
import { STAFF_PIN_PATTERN, setMyStaffPin } from "@/api/user/staff-pin";

/** Wrong guesses allowed before the device is signed out. */
export const STAFF_PIN_MAX_ATTEMPTS = 5;
type StaffPinInputProps = Omit<React.ComponentProps<typeof Input>, "type" | "inputMode" | "pattern" | "maxLength" | "onChange" | "value"> & {
  value: string;
  onChange: (value: string) => void;
};

/** Numeric-keypad PIN field. Strips anything that isn't a digit and caps at 4. */
export const StaffPinInput = forwardRef<HTMLInputElement, StaffPinInputProps>(function StaffPinInput(t0, ref) {
  const $ = _c(11);
  let onChange;
  let props;
  let value;
  if ($[0] !== t0) {
    ({
      value,
      onChange,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = onChange;
    $[2] = props;
    $[3] = value;
  } else {
    onChange = $[1];
    props = $[2];
    value = $[3];
  }
  let t1;
  if ($[4] !== onChange) {
    t1 = e => onChange(e.target.value.replace(/\D/g, "").slice(0, 4));
    $[4] = onChange;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1 || $[9] !== value) {
    t2 = <Input ref={ref} type="password" inputMode="numeric" pattern="[0-9]*" maxLength={4} autocomplete="off" value={value} onChange={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = value;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
});

/** New PIN + confirmation. Saves to the server and refreshes the current user
 *  so `hasPin` flips everywhere at once. */
export const StaffPinSetupForm = t0 => {
  const $ = _c(52);
  const {
    onSaved,
    submitLabel: t1,
    autoFocus: t2,
    onCancel,
    compact: t3
  } = t0;
  const submitLabel = t1 === undefined ? "Save PIN" : t1;
  const autoFocus = t2 === undefined ? false : t2;
  const compact = t3 === undefined ? false : t3;
  const {
    refetchUser,
    setCurrentUser
  } = useUser();
  const {
    toast
  } = useToast();
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  let t4;
  if ($[0] !== pin) {
    t4 = STAFF_PIN_PATTERN.test(pin);
    $[0] = pin;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const pinValid = t4;
  const mismatch = confirm.length > 0 && confirm !== pin;
  let t5;
  if ($[2] !== confirm || $[3] !== onSaved || $[4] !== pin || $[5] !== pinValid || $[6] !== refetchUser || $[7] !== setCurrentUser || $[8] !== toast) {
    t5 = async e => {
      e.preventDefault();
      if (!pinValid) {
        setError("PIN must be exactly 4 digits.");
        return;
      }
      if (confirm !== pin) {
        setError("PINs do not match.");
        return;
      }
      setSaving(true);
      setError(null);
      const ok = await setMyStaffPin(pin);
      setSaving(false);
      if (!ok) {
        setError("Could not save your PIN. Please try again.");
        return;
      }
      setCurrentUser(_temp);
      try {
        await refetchUser();
      } catch {}
      setPin("");
      setConfirm("");
      toast({
        title: "PIN saved",
        variant: "success"
      });
      onSaved?.();
    };
    $[2] = confirm;
    $[3] = onSaved;
    $[4] = pin;
    $[5] = pinValid;
    $[6] = refetchUser;
    $[7] = setCurrentUser;
    $[8] = toast;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const submit = t5;
  const t6 = compact ? "flex flex-wrap gap-4" : "space-y-4";
  const t7 = compact ? "w-32" : undefined;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Label htmlFor="staff-pin-new">New PIN</Label>;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== autoFocus || $[12] !== pin) {
    t9 = <StaffPinInput id="staff-pin-new" value={pin} onChange={setPin} placeholder="4 digits" className="mt-1" autoFocus={autoFocus} />;
    $[11] = autoFocus;
    $[12] = pin;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t7 || $[15] !== t9) {
    t10 = <div className={t7}>{t8}{t9}</div>;
    $[14] = t7;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = compact ? "w-32" : undefined;
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Label htmlFor="staff-pin-confirm">Confirm PIN</Label>;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const t13 = mismatch && !compact ? "PINs do not match." : undefined;
  let t14;
  if ($[18] !== confirm || $[19] !== mismatch || $[20] !== t13) {
    t14 = <StaffPinInput id="staff-pin-confirm" value={confirm} onChange={setConfirm} className="mt-1" hasError={mismatch} errorMessage={t13} />;
    $[18] = confirm;
    $[19] = mismatch;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t11 || $[23] !== t14) {
    t15 = <div className={t11}>{t12}{t14}</div>;
    $[22] = t11;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t10 || $[26] !== t15 || $[27] !== t6) {
    t16 = <div className={t6}>{t10}{t15}</div>;
    $[25] = t10;
    $[26] = t15;
    $[27] = t6;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== compact || $[30] !== error) {
    t17 = error && !compact ? <p className="text-body text-status-error-text" role="alert">{error}</p> : null;
    $[29] = compact;
    $[30] = error;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== onCancel || $[33] !== saving) {
    t18 = onCancel ? <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>Cancel</Button> : null;
    $[32] = onCancel;
    $[33] = saving;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  const t19 = !pinValid || confirm !== pin;
  let t20;
  if ($[35] !== saving || $[36] !== submitLabel || $[37] !== t19) {
    t20 = <Button type="submit" variant="primary" isLoading={saving} loadingText="Saving..." disabled={t19}>{submitLabel}</Button>;
    $[35] = saving;
    $[36] = submitLabel;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== compact || $[40] !== error || $[41] !== mismatch) {
    t21 = compact && (error || mismatch) ? <p className="ml-2 text-body text-status-error-text" role="alert">{error ?? "PINs do not match."}</p> : null;
    $[39] = compact;
    $[40] = error;
    $[41] = mismatch;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t18 || $[44] !== t20 || $[45] !== t21) {
    t22 = <div className="flex flex-wrap items-center gap-2">{t18}{t20}{t21}</div>;
    $[43] = t18;
    $[44] = t20;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== submit || $[48] !== t16 || $[49] !== t17 || $[50] !== t22) {
    t23 = <form onSubmit={submit} className="space-y-4">{t16}{t17}{t22}</form>;
    $[47] = submit;
    $[48] = t16;
    $[49] = t17;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  return t23;
};

/** Full-page "set a PIN before you start" screen shown to staff who open an
 *  in-person form without a PIN. They are still holding the device here.
 *  `relative z-10` lifts it above the console's fixed grid background, which
 *  otherwise paints over content on sidebar-less routes (see tip-screen,
 *  onboarding, FormSkeleton). */
export const StaffPinSetupScreen = () => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <IoLockClosedOutline className="h-8 w-8 text-foreground-muted" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h1 className="text-xl font-semibold text-foreground"><span>Set a staff PIN</span></h1>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="relative z-10 flex h-full w-full flex-col items-center justify-center bg-background p-6"><div className="w-full max-w-sm rounded-lg border border-border bg-card p-6 shadow-sm"><div className="mb-4 flex flex-col items-center gap-2 text-center">{t0}{t1}<p className="text-body text-muted-foreground"><span>For HIPAA compliance, set a personal 4-digit PIN to lock form pages from clients. You'll enter it to return to Decoda after a patient presses Done. Change it any time from your profile page.</span></p></div><StaffPinSetupForm submitLabel="Save PIN and continue" autoFocus={true} /></div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};

/** Shared PIN entry: the input, unlock button, error, and a forgot-PIN sign
 *  out. Rendered inside whatever container each gate provides. */
export const StaffPinUnlockForm = t0 => {
  const $ = _c(28);
  const {
    onUnlocked,
    autoFocus: t1
  } = t0;
  const autoFocus = t1 === undefined ? true : t1;
  const {
    pin,
    onPinChange,
    error,
    checking,
    signingOut,
    submit,
    signOut
  } = useStaffPinUnlock(onUnlocked);
  const t2 = !!error;
  const t3 = error ?? undefined;
  let t4;
  if ($[0] !== autoFocus || $[1] !== onPinChange || $[2] !== pin || $[3] !== t2 || $[4] !== t3) {
    t4 = <StaffPinInput aria-label="Staff PIN" value={pin} onChange={onPinChange} hasError={t2} errorMessage={t3} autoFocus={autoFocus} />;
    $[0] = autoFocus;
    $[1] = onPinChange;
    $[2] = pin;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== checking || $[7] !== pin || $[8] !== signingOut) {
    t5 = !STAFF_PIN_PATTERN.test(pin) || checking || signingOut;
    $[6] = checking;
    $[7] = pin;
    $[8] = signingOut;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = checking ? "Checking\u2026" : "Unlock";
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <Button variant="primary" type="submit" className="w-full" disabled={t5}>{t6}</Button>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== submit || $[14] !== t4 || $[15] !== t7) {
    t8 = <form onSubmit={submit} className="space-y-4">{t4}{t7}</form>;
    $[13] = submit;
    $[14] = t4;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span>Forgot your PIN? </span>;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const t10 = signingOut || checking;
  const t11 = signingOut ? "Signing out\u2026" : "Sign out";
  let t12;
  if ($[18] !== signOut || $[19] !== t10 || $[20] !== t11) {
    t12 = <button type="button" className="underline hover:text-foreground disabled:opacity-60" onClick={signOut} disabled={t10}>{t11}</button>;
    $[18] = signOut;
    $[19] = t10;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span> and sign back in.</span>;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t12) {
    t14 = <div className="mt-4 text-center text-caption text-foreground-muted">{t9}{t12}{t13}</div>;
    $[23] = t12;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t14 || $[26] !== t8) {
    t15 = <>{t8}{t14}</>;
    $[25] = t14;
    $[26] = t8;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  return t15;
};

/** Non-dismissible dialog asking for the staff PIN, used as the Done gate on the
 *  in-person form tab. Radix traps focus and marks the page behind it
 *  aria-hidden; Escape and outside clicks are swallowed. */
export const StaffPinUnlockOverlay = t0 => {
  const $ = _c(13);
  const {
    open,
    onUnlocked,
    title: t1,
    description: t2
  } = t0;
  const title = t1 === undefined ? "Staff only" : t1;
  const description = t2 === undefined ? "Enter your staff PIN to return to Decoda." : t2;
  if (!open) {
    return null;
  }
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <IoLockClosedOutline className="h-8 w-8 text-foreground-muted" />;
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== title) {
    t4 = <DialogTitle className="text-xl font-semibold text-foreground">{title}</DialogTitle>;
    $[1] = title;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== description) {
    t5 = <DialogDescription className="text-body text-muted-foreground">{description}</DialogDescription>;
    $[3] = description;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t4 || $[6] !== t5) {
    t6 = <div className="mb-4 flex flex-col items-center gap-2 text-center">{t3}{t4}{t5}</div>;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== onUnlocked) {
    t7 = <StaffPinUnlockForm onUnlocked={onUnlocked} />;
    $[8] = onUnlocked;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t6 || $[11] !== t7) {
    t8 = <Dialog open={true}><DialogContent className="max-w-sm p-6" onEscapeKeyDown={_temp2} onInteractOutside={_temp3}>{t6}{t7}</DialogContent></Dialog>;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
};
function _temp(prev) {
  return prev ? {
    ...prev,
    hasPin: true
  } : prev;
}
function _temp2(e) {
  return e.preventDefault();
}
function _temp3(e_0) {
  return e_0.preventDefault();
}